import {useCallback, useEffect, useRef, useState} from "react";
import useAPIService from "../../hooks/useAPIService";
import {DOB_RESOLVED_EVENT, readStoredDob, storeDob} from "./dobSession";

// Fetches the signed-in participant's case view (POST /my-case).
//
// Response contract (backend PR #48 + 08-14 revisions + DOB disambiguation):
//   data.referrals[]     open referrals in engagement-rigor order
//                        (TANF/EWS > ABAWD > JSAP), each with `work_plan` =
//                        the newest plan (components -> activities, weekly
//                        goals only — no actuals, no platform time)
//   data.documents[]     Active documents in the participant-visible
//                        categories (allowlisted server-side)
//   data.hours_this_month  {month "YYYY-MM", hours, last_updated,
//                        weeks_counted} summed server-side from weekly
//                        check-ins, or null when nothing is on file (renders
//                        the blank state). Older deployed backends omit the
//                        key; treat absent as null.
//   data.needs_dob       true when multiple CRM contacts share this login
//                        id; no case data until date_of_birth matches.
//   data.dob_error       "no_match" | "ambiguous" after a failed DOB attempt.
//                        submitDob uses "request_error" for network/API
//                        failures so the modal does not call those a mismatch.
//   data.first_name /    CRM participant display name (allowlisted). Used by
//   data.last_name       the header after DOB on shared logins; single-
//                        participant accounts keep showing the DynamoDB name.
//
// The server decides identity from the verified JWT; customer_id is only
// echoed for the self-check.
//
// One request is shared across mounts. App (orientation lock), the DOB gate,
// Home, My Work Plan, My Documents and the video page each mount this hook;
// before, each fired its own POST /my-case (QA 10-05: 5 calls of 6-17 s per
// page load). Concurrent mounts now share the in-flight request and later
// mounts reuse the answer for CASE_CACHE_MS. The cache is keyed by login id
// AND stored DOB (a shared login never sees another member's case), errors
// are never cached, refetch() always goes to the server, and sign-out clears
// it (clearMyCaseCache), so an account switch can't show a stale case.
const CASE_CACHE_MS = 60 * 1000;
const caseCache = new Map(); // key -> {promise, settledAt}

const cacheKey = (customerId, dob) => `${customerId}|${dob || ""}`;

export const clearMyCaseCache = () => caseCache.clear();

// Exported so sign-in can read the CRM name through the same cache.
export const requestCase = (
  apiFetch,
  customerId,
  dob,
  {force = false} = {},
) => {
  const key = cacheKey(customerId, dob);
  const hit = caseCache.get(key);
  const fresh =
    hit &&
    (hit.settledAt === null || Date.now() - hit.settledAt < CASE_CACHE_MS);
  if (fresh && !force) return hit.promise;

  const body = {customer_id: customerId};
  if (dob) body.date_of_birth = dob;
  const entry = {settledAt: null};
  entry.promise = apiFetch("/my-case", {
    method: "POST",
    body: JSON.stringify(body),
  }).then(
    (res) => {
      // Only a successful answer is reused; anything else is retried by the
      // next caller instead of being served for CASE_CACHE_MS.
      if (res?.result === "success") entry.settledAt = Date.now();
      else if (caseCache.get(key) === entry) caseCache.delete(key);
      return res;
    },
    (err) => {
      if (caseCache.get(key) === entry) caseCache.delete(key);
      throw err;
    },
  );
  caseCache.set(key, entry);
  return entry.promise;
};

// status: "loading" | "success" | "error" | "needs_dob"
const useMyCase = (loggedInUser) => {
  const {apiFetch} = useAPIService();
  const [status, setStatus] = useState("loading");
  const [caseData, setCaseData] = useState(null);
  const [dobError, setDobError] = useState(null);
  const [dobSubmitting, setDobSubmitting] = useState(false);
  const customerId = loggedInUser?.customer_id;
  const statusRef = useRef(status);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const applySuccess = useCallback((data) => {
    setCaseData({
      referrals: Array.isArray(data?.referrals) ? data.referrals : [],
      documents: Array.isArray(data?.documents) ? data.documents : [],
      hoursThisMonth: data?.hours_this_month ?? null,
      firstName:
        typeof data?.first_name === "string" ? data.first_name.trim() : "",
      lastName:
        typeof data?.last_name === "string" ? data.last_name.trim() : "",
      // QA/Sandbox only: seconds that complete orientation for this learner
      // (fastOrientation.js). Absent for everyone in production.
      fastOrientationSeconds: data?.fast_orientation_seconds ?? null,
    });
    setDobError(null);
    statusRef.current = "success";
    setStatus("success");
  }, []);

  const fetchCase = useCallback(
    async ({force = false} = {}) => {
      if (!customerId) {
        setStatus("error");
        return;
      }
      setStatus("loading");
      setDobError(null);
      try {
        const {result, data} = await requestCase(
          apiFetch,
          customerId,
          readStoredDob(customerId),
          {force},
        );
        if (result !== "success") throw new Error("Request was not successful");

        if (data?.needs_dob) {
          setCaseData({
            referrals: [],
            documents: [],
            hoursThisMonth: null,
            firstName: "",
            lastName: "",
          });
          setDobError(data.dob_error || null);
          setStatus("needs_dob");
          return;
        }

        applySuccess(data);
      } catch (err) {
        console.error("Failed to load case:", err);
        setStatus("error");
      }
    },
    [apiFetch, customerId, applySuccess],
  );

  const submitDob = useCallback(
    async (dateOfBirth) => {
      if (!customerId || !dateOfBirth) return false;
      setDobSubmitting(true);
      setDobError(null);
      try {
        const {result, data} = await apiFetch("/my-case", {
          method: "POST",
          body: JSON.stringify({
            customer_id: customerId,
            date_of_birth: dateOfBirth,
          }),
        });
        if (result !== "success") throw new Error("Request was not successful");

        if (data?.needs_dob) {
          setCaseData({
            referrals: [],
            documents: [],
            hoursThisMonth: null,
            firstName: "",
            lastName: "",
          });
          setDobError(data.dob_error || "no_match");
          setStatus("needs_dob");
          return false;
        }

        applySuccess(data);
        // Seed the shared cache before storeDob: its DOB_RESOLVED_EVENT makes
        // every other mount refetch, and they should reuse this answer.
        caseCache.set(cacheKey(customerId, dateOfBirth), {
          settledAt: Date.now(),
          promise: Promise.resolve({result, data}),
        });
        storeDob(customerId, dateOfBirth);
        return true;
      } catch (err) {
        console.error("Failed to resolve case by DOB:", err);
        setDobError("request_error");
        setStatus("needs_dob");
        return false;
      } finally {
        setDobSubmitting(false);
      }
    },
    [apiFetch, customerId, applySuccess],
  );

  useEffect(() => {
    fetchCase();
  }, [fetchCase]);

  // Another mount (e.g. App DOB gate) resolved DOB — refetch with stored date.
  // Skip when this mount already succeeded (submitDob on this instance).
  useEffect(() => {
    if (!customerId) return undefined;
    const onResolved = (e) => {
      if (e?.detail?.customerId && e.detail.customerId !== customerId) return;
      if (statusRef.current === "success") return;
      fetchCase();
    };
    window.addEventListener(DOB_RESOLVED_EVENT, onResolved);
    return () => window.removeEventListener(DOB_RESOLVED_EVENT, onResolved);
  }, [customerId, fetchCase]);

  return {
    status,
    caseData,
    // Explicit retry/refresh always goes to the server.
    refetch: () => fetchCase({force: true}),
    submitDob,
    dobError,
    dobSubmitting,
  };
};

export default useMyCase;
